import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TrustSignalsSection = () => {
  const verificationBadges = [
    {
      icon: 'Shield',
      title: 'Verified Properties',
      description: 'All properties undergo thorough verification',
      count: '50,000+'
    },
    {
      icon: 'CheckCircle',
      title: 'Authenticated Owners',
      description: 'Direct connection with verified property owners',
      count: '25,000+'
    },
    {
      icon: 'Star',
      title: 'User Reviews',
      description: 'Genuine reviews from real customers',
      count: '100,000+'
    },
    {
      icon: 'Clock',
      title: 'Real-time Updates',
      description: 'Live property availability and pricing',
      count: '24/7'
    }
  ];

  const userTestimonials = [
    {
      id: 1,
      name: "Priya Sharma",
      role: "Software Engineer",
      location: "Bangalore",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      rating: 5,
      review: `Found my dream 2BHK in Koramangala within 3 days! The property details were accurate and the owner was genuine. PowerHouse made the entire process seamless.`,
      propertyType: "2 BHK Apartment",
      dealValue: "₹35,000/month"
    },
    {
      id: 2,
      name: "Rajesh Kumar",
      role: "Marketing Manager",
      location: "Mumbai",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      rating: 5,
      review: `Sold my property in Bandra within 2 weeks at the right price. The platform's market insights helped me price it correctly. Excellent service!`,
      propertyType: "3 BHK Villa",
      dealValue: "₹2.5 Cr"
    },
    {
      id: 3,
      name: "Anita Patel",
      role: "Business Owner",
      location: "Delhi",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      rating: 5,
      review: `The neighborhood intelligence feature was incredibly helpful. I could understand the area completely before making the decision. Highly recommended!`,
      propertyType: "4 BHK Penthouse",
      dealValue: "₹85,000/month"
    }
  ];

  const recentActivity = [
    { action: 'Property Inquiry', count: 1247, timeframe: 'Last 24 hours' },
    { action: 'New Listings', count: 89, timeframe: 'Today' },
    { action: 'Successful Deals', count: 156, timeframe: 'This week' },
    { action: 'User Registrations', count: 342, timeframe: 'Today' }
  ];

  const trustPartners = [
    {
      name: "RERA Certified",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop",
      description: "Government approved platform"
    },
    {
      name: "ISO Certified",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop",
      description: "Quality management certified"
    },
    {
      name: "Secure Payments",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop",
      description: "Bank-grade security"
    },
    {
      name: "Legal Support",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop",
      description: "Expert legal assistance"
    }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? 'text-warning fill-current' : 'text-muted'}
      />
    ));
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Trusted by Thousands
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Join millions of satisfied customers who found their perfect properties through our verified platform
          </p>
        </div>

        {/* Verification Badges */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {verificationBadges.map((badge, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={badge.icon} size={32} className="text-primary" />
              </div>
              <div className="text-2xl font-bold text-text-primary mb-2">
                {badge.count}
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                {badge.title}
              </h3>
              <p className="text-sm text-text-secondary">
                {badge.description}
              </p>
            </div>
          ))}
        </div>

        {/* User Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-text-primary text-center mb-8">
            What Our Customers Say
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {userTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-canvas rounded-xl p-6 shadow-property-card border border-border">
                {/* User Info */}
                <div className="flex items-center mb-4">
                  <Image
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-text-primary">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {testimonial.role} • {testimonial.location}
                    </p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center mb-4">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Review */}
                <p className="text-text-secondary mb-4 text-sm leading-relaxed">
                  {testimonial.review}
                </p>

                {/* Deal Info */}
                <div className="bg-muted rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Property:</span>
                    <span className="font-medium text-text-primary">
                      {testimonial.propertyType}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-text-secondary">Deal Value:</span>
                    <span className="font-semibold text-primary">
                      {testimonial.dealValue}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity Counter */}
        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-text-primary text-center mb-8">
            Live Activity Dashboard
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {recentActivity.map((activity, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {activity.count.toLocaleString()}
                </div>
                <div className="font-medium text-text-primary mb-1">
                  {activity.action}
                </div>
                <div className="text-sm text-text-secondary">
                  {activity.timeframe}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Partners */}
       {/* <div className="text-center">
          <h3 className="text-xl font-semibold text-text-primary mb-8">
            Certified & Trusted Platform
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            {trustPartners.map((partner, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-10 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Image
                    src={partner.logo}
                    alt={partner.name}
                    className="w-full h-full object-contain opacity-60"
                  />
                </div>
                <div className="font-medium text-text-primary text-sm mb-1">
                  {partner.name}
                </div>
                <div className="text-xs text-text-secondary">
                  {partner.description}
                </div>
              </div>
            ))}
          </div>
        </div>
        */}
        {/* CTA Section */}
        <div className="text-center mt-12">
          <div className="bg-primary/5 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Ready to Find Your Perfect Property?
            </h3>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              Join thousands of satisfied customers and discover your ideal home today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Search"
                iconPosition="left"
              >
                Start Property Search
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Plus"
                iconPosition="left"
              >
                List Your Property
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSignalsSection;